import React, { ComponentProps, memo } from 'react';

import { BaseStatusMessageProps } from '../../types/BaseStatusMessageProps';
import { SharedToggleProps } from '../../types/SharedToggleProps';
import { Actions } from '../Actions/Actions';
import { Layout } from '../Layout/Layout';
import { Markdown } from '../Markdown/Markdown';
import { StatusMessage } from '../StatusMessage/StatusMessage';
import { Text } from '../Text/Text';
import { Toggle } from '../Toggle/Toggle';

interface Props {
  /** Section contents */
  children: React.ReactNode;
  /** Section title */
  title: string;
  /** Section actions */
  actions?: Pick<
    ComponentProps<typeof Actions>,
    'actions' | 'moreActions' | 'morePopover'
  >;
  /** Alignment of section content */
  align?: 'flex-start' | 'center' | 'flex-end' | 'stretch';
  /** Section description */
  description?: string;
  /** Disables the section and applies disabled styling */
  disabled?: boolean;
  /** Heading level */
  heading?: 'h3' | 'h4';
  /** HTML ID */
  id?: string;
  /** Section Status Message */
  statusMessage?: BaseStatusMessageProps;
  /** Display an interactive toggle */
  toggle?: SharedToggleProps;
}

export const Section = memo(
  ({
    actions,
    align,
    children,
    description,
    disabled,
    heading = 'h3',
    id,
    statusMessage,
    title,
    toggle,
  }: Props) => {
    const isH3 = heading === 'h3';
    const variant = isH3 ? 'section' : 'body-bold';

    return (
      <Layout
        as="section"
        direction="column"
        id={id}
        spacing={2}
        disabled={disabled}
      >
        <Layout direction="column">
          <Layout align="center" justify="space-between">
            <Text as={heading} m={0} variant={variant}>
              {title}
            </Text>
            <Layout align="center" preset="s">
              {toggle && <Toggle {...toggle} />}
              {actions && <Actions {...actions} />}
            </Layout>
          </Layout>
          {description && <Markdown preset="description" text={description} />}
        </Layout>
        {statusMessage && <StatusMessage {...statusMessage} />}
        {/* Pass align property to a new Layout that wraps children */}
        {align ? <Layout align={align}>{children}</Layout> : children}
      </Layout>
    );
  },
);
